// services/FactureService.js
import { useState, useEffect, useCallback } from "react";
import FactureModel from "../models/FactureModel";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux"; // comme useGetDevis

export const base_url = "http://localhost:8000/api/v1/";



export function useGetFactures(
    initialStatut = "Tous",
    initialDateRange = null,
    customToken = null // ✅ token passé en paramètre
) {
    const [factures, setFactures] = useState([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [pagination, setPagination] = useState({
        total: 0,
        per_page: 6,
        current_page: 1,
        last_page: 1,
    });
    const [statut, setStatut] = useState(initialStatut);
    const [dateRange, setDateRange] = useState(
        initialDateRange || { startDate: null, endDate: null }
    );
    const [nomClient, setNomClient] = useState("");
    const [numeroFacture, setNumeroFacture] = useState("");

    const reduxToken = useSelector((state) => state.auth.token);
    const token = customToken || reduxToken; // ✅ si aucun token passé, utiliser celui de Redux
    const navigate = useNavigate();

    const fetchFactures = useCallback(
        async (
            customPage = 1,
            filterStatut = statut,
            range = dateRange,
            client = nomClient,
            numero = numeroFacture
        ) => {
            if (!token) {
                navigate("/login");
                return;
            }

            setLoading(true);
            try {
                const url = new URL(`${base_url}factures`);
                url.searchParams.append("page", customPage);
                url.searchParams.append("per_page", pagination.per_page);
                url.searchParams.append("statut", filterStatut || "Tous");

                // Filtres de date
                if (range.startDate)
                    url.searchParams.append(
                        "date_debut_emission",
                        range.startDate.toISOString().split("T")[0]
                    );
                if (range.endDate)
                    url.searchParams.append(
                        "date_fin_emission",
                        range.endDate.toISOString().split("T")[0]
                    );

                // Filtres client & numéro de facture
                if (client.trim() !== "")
                    url.searchParams.append("client", client.trim());
                if (numero.trim() !== "")
                    url.searchParams.append("numero_facture", numero.trim());

                const response = await fetch(url, {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                });

                if (!response.ok)
                    throw new Error("Erreur lors de la récupération des factures");

                const data = await response.json();
                const facturesFormatées = data.factures.map(
                    (item) => new FactureModel(item)
                );

                setFactures(facturesFormatées);
                setPagination({
                    total: data.total,
                    per_page: data.per_page,
                    current_page: data.current_page,
                    last_page: Math.ceil(data.total / data.per_page),
                });
            } catch (error) {
                console.error("Erreur:", error);
                setFactures([]);
            } finally {
                setLoading(false);
            }
        },
        [token, navigate, pagination.per_page, statut, dateRange, nomClient, numeroFacture]
    );

    useEffect(() => {
        fetchFactures(page, statut, dateRange, nomClient, numeroFacture);
    }, [fetchFactures, page, statut, dateRange, nomClient, numeroFacture]);

    return {
        factures,
        loading,
        pagination,
        page,
        setPage,
        fetchFactures,
        setFactures,
        setLoading,
        setPagination,
        statut,
        setStatut,
        dateRange,
        setDateRange,
        nomClient,
        setNomClient,
        numeroFacture,
        setNumeroFacture,
    };
}


// ✅ Créer une facture manuellement

export async function addFacture(factureData) {
    // Récupération du token
    const token = localStorage.getItem("authTokenUtilisateur");
    if (!token) throw new Error("Utilisateur non authentifié");

    // Calcul automatique du reste dû si non fourni
    const acompte = factureData.acompte_centimes || 0;
    const total = factureData.total_centimes || 0;
    const resteDu = factureData.reste_du_centimes ?? Math.max(total - acompte, 0);

    // Préparation du payload
    const payload = {
        client_id: factureData.client_id,
        devis_id: factureData.devis_id || null,
        numero: factureData.numero?.trim() || "",
        date_emission: factureData.date_emission,
        date_echeance: factureData.date_echeance || null,
        statut: factureData.statut || "brouillon",
        sous_total_centimes: factureData.sous_total_centimes || 0,
        remise_centimes: factureData.remise_centimes || 0,
        tva_centimes: factureData.tva_centimes || 0,
        total_centimes: total,
        acompte_centimes: acompte,
        reste_du_centimes: resteDu,
        type_paiement: factureData.type_paiement || null,
        devise: factureData.devise || "EUR",
        lignes: (factureData.lignes || []).map((ligne) => ({
            designation: ligne.designation?.trim() || "",
            quantite: parseFloat(ligne.quantite) || 1,
            unite: ligne.unite?.trim() || null,
            prix_unitaire_centimes: parseInt(ligne.prix_unitaire_centimes) || 0,
            taux_tva: parseFloat(ligne.taux_tva) || 0,
            taux_remise: parseFloat(ligne.taux_remise) || 0,
            total_ligne_centimes: parseInt(ligne.total_ligne_centimes) || 0,
        })),
        // Ajout des secteurs sélectionnés
        secteurs_batiment: factureData.secteurs_batiment || [], // tableau d'id
        secteurs_autres: factureData.secteurs_autres || [],     // tableau d'id
    };

    // Envoi de la requête
    const response = await fetch(`${base_url}factures`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
    });

    // Gestion du JSON
    let data;
    try {
        data = await response.json();
    } catch (err) {
        console.error("Erreur parsing JSON :", err);
        throw new Error("Le serveur a renvoyé une réponse invalide");
    }

    // Gestion des erreurs côté serveur
    if (!response.ok) {
        const message = data?.message || data?.error || "Erreur lors de l'ajout de la facture";
        throw new Error(message);
    }

    return data;
}


// ✅ Créer une facture depuis un devis
export async function createFactureFromDevis(devisId) {
    const token = localStorage.getItem("authTokenUtilisateur");
    if (!token) throw new Error("Utilisateur non authentifié");

    if (!devisId || isNaN(parseInt(devisId)))
        throw new Error("ID de devis invalide");

    const response = await fetch(`${base_url}factures/from-devis/${devisId}`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
        },
    });

    let data;
    try {
        data = await response.json();
    } catch (err) {
        console.error("Erreur parsing JSON :", err);
        throw new Error("Le serveur a renvoyé une réponse invalide");
    }

    if (!response.ok) {
        throw new Error(
            data.message || "Erreur lors du transfert du devis en facture"
        );
    }

    return data;
}

// ✅ Supprimer une facture
export async function deleteFacture(factureId) {
    const token = localStorage.getItem("authTokenUtilisateur");
    if (!token) throw new Error("Utilisateur non authentifié");

    const response = await fetch(`${base_url}factures/${factureId}`, {
        method: "DELETE",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
        },
    });

    let data;
    try {
        data = await response.json();
    } catch (err) {
        console.error("Erreur parsing JSON :", err);
        throw new Error("Le serveur a renvoyé une réponse invalide");
    }

    if (!response.ok)
        throw new Error(data.message || "Erreur lors de la suppression");

    return data;
}

export async function getFacture(id) {
    //console.log("getFacture appelé avec id :", id);

    const token = localStorage.getItem("authTokenUtilisateur");
    // console.log("Token récupéré :", token);

    if (!token) throw new Error("Utilisateur non authentifié");

    if (!id || isNaN(parseInt(id))) throw new Error("ID de facture invalide");

    let response;
    try {
        response = await fetch(`${base_url}factures/${id}`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
        });
        //console.log("Response status :", response.status);
    } catch (fetchError) {
        //console.error("Erreur lors du fetch :", fetchError);
        throw new Error("Erreur réseau lors de la récupération de la facture");
    }

    let data;
    try {
        data = await response.json();
        //console.log("Data reçue :", data);
    } catch (err) {
        // console.error("Erreur parsing JSON :", err);
        throw new Error("Le serveur a renvoyé une réponse invalide");
    }

    if (!response.ok) {
        // console.error("Erreur response.ok :", data.message);
        throw new Error(data.message || "Erreur lors de la récupération");
    }

    const facture = new FactureModel(data.facture);
    //console.log("Facture instanciée :", facture);

    return facture;
}


// ✅ Mettre à jour une facture
export async function updateFacture(id, factureData) {
    const token = localStorage.getItem("authTokenUtilisateur");
    if (!token) throw new Error("Utilisateur non authentifié");

    if (!id || isNaN(parseInt(id))) throw new Error("ID de facture invalide");

    const response = await fetch(`${base_url}factures/${id}`, {
        method: "PUT",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(factureData),
    });

    let data;
    try {
        data = await response.json();
    } catch (err) {
        console.error("Erreur parsing JSON :", err);
        throw new Error("Le serveur a renvoyé une réponse invalide");
    }

    if (!response.ok)
        throw new Error(data.message || "Erreur lors de la mise à jour");

    return data;
}

// ✅ Récupérer toutes les factures avec pagination & statut

